import React from 'react';
import { useIntl } from 'react-intl';
import { MAX_AUDIO_DURATION_SECONDS } from '../../../constants/file.constants';
import {
    PAUSE_RECORDING_TEXT,
    PLAY_RECORDING_TEXT,
    RE_RECORD_TEXT,
    RECORDING_UNAVAILABLE_TEXT,
    START_RECORDING_TEXT,
    STOP_RECORDING_TEXT,
} from './record-audio.text';
import MicrophoneSVG from '../../../images/svg/microphone';
import StopSVG from '../../../images/svg/stop';
import { AudioWaveTrack } from '../audio-wave/audio-wave-track';
import { useRecordAudioWave } from './use-record-audio-wave';

import '../audio-wave/audio-wave.scss';

interface IRecordAudioWaveProps {
    maxDurationSeconds?: number;
    onChange?: (file: File | null) => void;
}

const RecordAudioWave = ({
    maxDurationSeconds = MAX_AUDIO_DURATION_SECONDS,
    onChange,
}: IRecordAudioWaveProps) => {
    const intl = useIntl();
    const {
        waveRef,
        status,
        isPlaying,
        playbackSeconds,
        durationSeconds,
        elapsedSeconds,
        hasWave,
        micUnavailable,
        togglePlay,
        startRecording,
        stopRecording,
    } = useRecordAudioWave({ maxDurationSeconds: maxDurationSeconds, onChange: onChange });

    const isRecording = status === 'recording';
    const isRecorded = status === 'recorded';
    const showPlaybackPosition = isPlaying || (playbackSeconds > 0 && playbackSeconds < durationSeconds);
    const displaySeconds = isRecorded
        ? (showPlaybackPosition ? playbackSeconds : durationSeconds)
        : elapsedSeconds;

    return (
        <div className='audio-wave'>
            <AudioWaveTrack
                waveRef={waveRef}
                isPlaying={isPlaying}
                hasWave={hasWave}
                seconds={displaySeconds}
                playLabel={intl.formatMessage(isPlaying ? PAUSE_RECORDING_TEXT : PLAY_RECORDING_TEXT)}
                playable={isRecorded}
                onToggle={togglePlay}
                leading={isRecording
                    ? (
                        <span className='audio-wave__lead audio-wave__lead--live'>
                            <MicrophoneSVG />
                        </span>
                    )
                    : undefined}
            />
            {isRecording && (
                <button
                    type='button'
                    className='audio-wave__stop'
                    onClick={stopRecording}
                    aria-label={intl.formatMessage(STOP_RECORDING_TEXT)}
                >
                    <StopSVG />
                </button>
            )}
            {isRecorded && (
                <button
                    type='button'
                    className='audio-wave__rerecord'
                    onClick={startRecording}
                    disabled={micUnavailable}
                    aria-label={intl.formatMessage(RE_RECORD_TEXT)}
                >
                    <MicrophoneSVG />
                    {intl.formatMessage(RE_RECORD_TEXT)}
                </button>
            )}
            {!isRecording && !isRecorded && (
                <button
                    type='button'
                    className='audio-wave__record'
                    onClick={startRecording}
                    disabled={micUnavailable}
                    aria-label={intl.formatMessage(START_RECORDING_TEXT)}
                >
                    <MicrophoneSVG />
                </button>
            )}
            {micUnavailable && (
                <p className='audio-wave__error' role='alert'>
                    {intl.formatMessage(RECORDING_UNAVAILABLE_TEXT)}
                </p>
            )}
        </div>
    );
};

export {
    RecordAudioWave,
    IRecordAudioWaveProps,
};
